import { UntypedFormControl, Validators } from '@angular/forms';
import { SearchService } from '@core/services';
import { OiqFormBase } from '@oiq-ui-sdk/form';
import moment from 'moment';
import {
  BehaviorSubject,
  catchError,
  debounceTime,
  distinctUntilChanged,
  map,
  merge,
  Observable,
  of,
  switchMap,
  tap,
} from 'rxjs';
import {
  OIQ_NOTIFICATION_ENTITY_TYPES,
  OIQ_NOTIFICATION_REMINDER_INTERVALS,
} from './notification-dialog.data';

export class OiqNotificationForm extends OiqFormBase {
  minDate = new Date();

  searching$ = new BehaviorSubject<boolean>(false);
  entityType$ = new BehaviorSubject<string>('');
  entityData$ = new BehaviorSubject<any>(null);
  entityLink$ = merge(this.entityType$, this.entityData$).pipe(
    debounceTime(50),
    map(() => {
      const _data = this.value;
      if (!_data.entityId || !_data.entityType) {
        return '';
      }
      const _base = `/${_data.entityType}/${_data.entityId}`;

      switch (_data.entityType) {
        case 'law-firms':
        case 'isos':
          return `${_base}/general-info`;
        default:
          return `${_base}/overview`;
      }
    })
  );
  isCollectionDetail$ = this.entityType$.pipe(
    map(_entityType => _entityType == 'collections-detail')
  );
  entityTypes = OIQ_NOTIFICATION_ENTITY_TYPES;
  reminderIntervals = OIQ_NOTIFICATION_REMINDER_INTERVALS;
  reminderIntervals2: typeof OIQ_NOTIFICATION_REMINDER_INTERVALS = [];

  constructor(private searchService: SearchService) {
    super({
      id: new UntypedFormControl(null),
      date: new UntypedFormControl(null, [Validators.required]),
      time: new UntypedFormControl(null, [Validators.required]),
      interval: new UntypedFormControl(null, [Validators.required]),
      interval2: new UntypedFormControl(null),
      title: new UntypedFormControl(null, [Validators.required]),
      description: new UntypedFormControl(null),
      entityId: new UntypedFormControl(null),
      entityName: new UntypedFormControl(null),
      entityType: new UntypedFormControl(null),
    });

    this.get('interval').valueChanges.subscribe(_interval => {
      this.getReminderIntervals2(_interval);
    });

    this.get('entityType').valueChanges.subscribe(_entityType => {
      const _oldValue = this.entityType$.value;
      if (_oldValue == _entityType) {
        return;
      }
      this.patchValue({ entityId: null, entityName: null });
      this.entityType$.next(_entityType);
    });

    merge(this.get('entityId').valueChanges, this.get('entityName').valueChanges).subscribe(() => {
      this.entityData$.next({
        entityId: this.get('entityId').value,
        entityName: this.get('entityName').value,
      });
    });
  }

  getReminderIntervals2(interval) {
    const index = this.reminderIntervals.map(object => object.value).indexOf(interval);
    if (index === -1) {
      // not found
      return;
    }
    this.reminderIntervals2 = this.reminderIntervals.slice(index + 1);
  }

  selectEntity(_value: any): void {
    if (!_value) {
      this.patchValue({
        entityId: null,
        entityName: '',
      });
      return;
    }
    this.patchValue({
      entityId: Number(_value.id),
      entityName: String(_value.name),
    });
  }

  searchEntity = (text$: Observable<string>) =>
    text$.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      tap(() => this.searching$.next(true)),
      switchMap(term => this.searchEntityType(term)),
      catchError(() => of([])),
      tap(() => this.searching$.next(true))
    );

  searchEntityType(term: string) {
    const _entityType = this.value.entityType;
    switch (_entityType) {
      case 'clients':
        return this.searchService.performClientsTypeahead(term);
      case 'syndicators':
        return this.searchService.performSyndicatorsTypeahead(term);
      case 'isos':
        return this.searchService.performIsosTypeahead(term);
      case 'law-firms':
        return this.searchService.performLawFirmsTypeahead(term);
      default:
        return of([]);
    }
  }

  fromResponse(data: any): void {
    // date
    const _date = moment(data.eventDate);
    if (_date) {
      this.patchValue({
        date: _date.toDate(),
        time: _date.format('HH:mm'),
      });
    }
    // interval
    const _data = data || {};
    const _calendarReminders = _data.calendarReminders || [];
    const _reminder1 = _calendarReminders[0] || {};
    const _reminder2 = _calendarReminders[1] || {};
    const _interval = _reminder1 ? _reminder1.interval : null;
    const _interval2 = _reminder2 ? _reminder2.interval : '';
    const _unit = _reminder1 ? _reminder1.unit : null;

    // patch
    this.patchValue({
      id: data.id,
      interval: _interval,
      interval2: _interval2,
      title: data.title || '',
      description: data.description || '',
      entityId: data.entityId || null,
      entityName: data.entityName || '',
      entityType: data.entityType || '',
      unit: _unit,
    });
  }

  get toPayload(): any {
    const _data = this.value;

    // combine date/time
    const _date = moment(_data.date).format('DD/MM/YYYY');
    var dateParts = _date.split('/');
    const _dateDay: number = parseInt(dateParts[0] || '0');
    const _dateMonth: number = parseInt(dateParts[1] || '1');
    const _dateYear: number = parseInt(dateParts[2] || '0');
    var timeParts = _data.time.split(':');
    if (dateParts && timeParts) {
      const datetime = new Date(_dateYear, _dateMonth - 1, _dateDay, timeParts[0], timeParts[1]);
      _data.eventDate = datetime.toISOString();
    }

    // extra data
    _data.eventRead = false;
    _data.entityId = _data.entityId ? _data.entityId : null;

    _data.calendarReminders = [
      {
        reminderRead: false,
        interval: _data.interval,
        unit: 'Minutes',
      },
    ];

    if (_data.interval2) {
      _data.calendarReminders.push({
        reminderRead: false,
        interval: _data.interval2,
        unit: 'Minutes',
      });
    }

    return _data;
  }
}
